/**
 * レセプト内容一覧 — paginated search hook.
 * Reactive to serviceType (A2 ↔ R2) via ThemeContext.
 * Pass `null` as payload to skip the query.
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import useAppToast from '@/hooks/useAppToast';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { DEFAULT_FETCH_ERROR_MESSAGE, getApiErrorMessage } from '@/utils/apiError';
import { searchRezeptHistories } from '@/services/main/rezeptHistories';
import type {
  RezeptHistoriesSearchPayload,
  RezeptHistoriesSearchResult,
} from '@/pages/main/rezeptHistories/types';

export const useRezeptHistoriesSearch = (payload: RezeptHistoriesSearchPayload | null) => {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  const { data, isLoading, isFetching, isError, error, refetch } = useQuery<
    RezeptHistoriesSearchResult,
    Error
  >({
    queryKey: ['rezept-histories', 'search', tenant, clinicNumber, serviceType, payload],
    queryFn: () => searchRezeptHistories(tenant, clinicNumber, payload!, serviceType),
    enabled: !!tenant && !!clinicNumber && payload !== null,
    placeholderData: prev => prev,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading: isLoading || isFetching, refetch };
};
